/**
 * Unified conversion utilities
 *
 * This module provides a clean API for unit conversions while maintaining
 * backwards compatibility with the original convert.js exports.
 */

import { asDecimal } from '@/lib/helpers/numeric'

// Re-export all modules
export * from './datetime'
export * from './units'
export * from './distance'
export * from './weight'
export * from './temperature'
export * from './speed'
export * from './time'
export * from './misc'

// Import for internal use
import { units, getUnitKey, getUserUnit } from './units'
import { convertDistance } from './distance'
import { convertWeight } from './weight'
import { convertDegree } from './temperature'
import { convertSpeed } from './speed'
import { convertTime } from './time'

/**
 * Get supported units for validation
 */
const getSupportedUnits = () => ({
    distance: Object.keys(units['m']),
    weight: Object.keys(units['kg']),
    speed: Object.keys(units['km/h']),
    degree: Object.keys(units['C']),
    time: [...Object.keys(units['hh:mm:ss']), ...Object.keys(units['hh:mm']), ...Object.keys(units['mm:ss'])],
})

/**
 * Convert value using user's preferred units
 *
 * @param {number} value - Value to convert
 * @param {string} unitIn - Source unit
 * @param {object} userUnits - User's preferred units by category
 * @param {number} decimals - Number of decimals
 * @param {boolean} forceUnit - Don't auto-scale
 * @param {boolean} returnString - Return formatted string
 * @returns {object|string|null}
 */
export const convertValueUserUnits = (value, unitIn = null, userUnits = {}, decimals, forceUnit, returnString) => {
    if (!unitIn) return null
    const unitKey = getUnitKey(unitIn)
    const userUnit = userUnits?.[unitKey]
    return convertValueUnit(value, unitIn, userUnit, decimals, forceUnit, returnString)
}

/**
 * Convert value between units with automatic type detection
 *
 * @param {number} value - Value to convert
 * @param {string} unitIn - Source unit
 * @param {string} unitOut - Target unit
 * @param {number} decimals - Number of decimals
 * @param {boolean} forceUnit - Don't auto-scale
 * @param {boolean} returnString - Return formatted string
 * @returns {object|string}
 */
export const convertValueUnit = (value, unitIn, unitOut, decimals, forceUnit, returnString) => {
    const emptyValue = {
        value: decimals || decimals === 0 ? asDecimal(value, decimals) : value,
        unit: unitOut ? unitOut : unitIn,
    }

    const supported = getSupportedUnits()
    const allSupported = [
        ...supported.distance,
        ...supported.weight,
        ...supported.speed,
        ...supported.degree,
        ...supported.time,
    ]

    // Validate units
    if (!allSupported.includes(unitIn) || !allSupported.includes(unitOut)) {
        return returnString ? `${emptyValue.value} ${emptyValue.unit}` : emptyValue
    }

    // Validate value
    if (!unitIn || !unitOut || (isNaN(value) && !supported.time.includes(unitIn)) || value === null || value === '') {
        return returnString ? `${emptyValue.value} ${emptyValue.unit}` : emptyValue
    }

    // Route to appropriate converter
    if (supported.distance.includes(unitIn)) {
        return convertDistance(value, unitIn, unitOut, forceUnit, decimals, returnString)
    }
    if (supported.weight.includes(unitIn)) {
        return convertWeight(value, unitIn, unitOut, decimals, returnString)
    }
    if (supported.speed.includes(unitIn)) {
        return convertSpeed(value, unitIn, unitOut, decimals, returnString)
    }
    if (supported.degree.includes(unitIn)) {
        return convertDegree(value, unitIn, unitOut, decimals, returnString)
    }
    if (supported.time.includes(unitIn)) {
        return convertTime(value, unitIn, unitOut, returnString)
    }

    throw new Error('Unit not supported')
}
